import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Input, ModalUI, Selector, SelectorOptionPropsInterface } from 'sonata-react-components';

import { getPermanenceDefaultStayFeatures } from '@/helpers/pixel/pixel.helper';
import { getUserId } from '@/helpers/user/user.helpers';
import { getPixelPlacementOptions, getPixelTypeOptions } from '../helpers';

import './newPixel.scss';

import { savePixel, updatePixel } from './helpers';

import type { Pixel } from 'sonata-static-storage/lib/schemas/pixel/pixel.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

type Props = {
  pixel: Pixel | null;
  onClose: () => void;
  onSave: () => void;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

type State = {
  name: Pixel['name'];
  type: Pixel['type'] | null;
  code: Pixel['codeType'] | null;
  placement: Pixel['placement'] | null;
  features: Pixel['features'];
  isSaving: boolean;
};

export function NewPixel({ pixel, onClose, onSave, user, userRoles }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const modalRef = useRef<React.ElementRef<typeof ModalUI>>(null);
  const [{ name, type, code, placement, features, isSaving }, setState] = useState<State>({
    code: pixel?.codeType ?? null,
    features: pixel?.features || {
      stayTime: []
    },
    isSaving: false,
    name: pixel?.name ?? '',
    placement: pixel?.placement ?? null,
    type: pixel?.type ?? null
  });

  const BASE_ID = 'newPixel';

  useEffect(() => {
    modalRef?.current?.openModal();
  }, [modalRef]);

  const handleNameChange = (name: Pixel['name']) => {
    setState(prev => ({
      ...prev,
      name: name?.slice(0, 30) ?? null
    }));
  };

  const handleChangeType = (option: SelectorOptionPropsInterface) => {
    const pixelType = option.id as Pixel['type'];
    const isStayType = pixelType === 'stay';
    setState(prev => ({
      ...prev,
      code: isStayType ? 'script' : code,
      features: {
        ...pixel?.features,
        stayTime: isStayType ? getPermanenceDefaultStayFeatures() : []
      },
      placement: null,
      type: pixelType
    }));
  };

  const handleChangePlacement = (option: SelectorOptionPropsInterface) => {
    setState(prev => ({
      ...prev,
      placement: option.id as Pixel['placement']
    }));
  };

  const token = {};
  const handleSave = async () => {
    setState(prev => ({ ...prev, isSaving: true }));
    const pixelConfig: Partial<Pixel> = {
      ...pixel,
      codeType: code ?? undefined,
      features,
      name,
      placement: placement ?? undefined,
      type: type ?? undefined
    };
    pixel
      ? await updatePixel(pixelConfig as Pixel, formatMessage, token)
      : await savePixel(pixelConfig, getUserId(user), formatMessage, token);
    setState(prev => ({ ...prev, isSaving: false }));
    onSave();
  };

  const handleClose = () => {
    onClose();
  };

  const pixelTypeOptions = getPixelTypeOptions(user, userRoles, formatMessage);
  const pixelPlacementOptions = getPixelPlacementOptions(type, formatMessage);
  const isCreateButtonDisabled = !name || !type || !placement || isSaving;

  return (
    <ModalUI
      closeModalCallback={() => handleClose()}
      leftButton={
        <ButtonUI
          className="newPixel_backButton"
          icon="fa-solid fa-arrow-left"
          onClick={() => handleClose()}
          text={formatMessage({
            defaultMessage: 'Back',
            id: 'common.back'
          })}
          type="secondary"
        />
      }
      ref={modalRef}
      rightButton={
        <ButtonUI
          className="newPixel_createButton"
          disabled={isCreateButtonDisabled}
          icon="fa-regular fa-floppy-disk"
          loading={isSaving}
          onClick={handleSave}
          text={
            pixel
              ? formatMessage({
                  defaultMessage: 'Update',
                  id: 'common.update'
                })
              : formatMessage({
                  defaultMessage: 'Create',
                  id: 'common.create'
                })
          }
          type="primary"
        />
      }
      title={
        pixel?.name
          ? formatMessage({
              defaultMessage: 'Pixel creation',
              id: 'menu.newPixel.title'
            })
          : formatMessage({
              defaultMessage: 'Pixel details',
              id: 'menu.newPixel.details'
            })
      }
      width="678px"
    >
      <div className="newPixel">
        <Input
          id={`${BASE_ID}_name`}
          label={formatMessage({
            defaultMessage: 'Name',
            id: 'menu.newPixel.name'
          })}
          name="name"
          onChange={value => handleNameChange(value as string)}
          placeholder={formatMessage({
            defaultMessage: 'Type a name...',
            id: 'menu.newPixel.namePlaceholder'
          })}
          shape="squareUI"
          type="text"
          value={name}
        />
        <div className="newPixel__selectors">
          <Selector
            className="newPixel__type"
            id={`${BASE_ID}_pixelTypeSelector`}
            label={formatMessage({
              defaultMessage: 'Pixel Type',
              id: 'menu.newPixel.pixelType.label'
            })}
            onChange={handleChangeType}
            options={pixelTypeOptions}
            showSearch={false}
            showSelectedItems={false}
            tooltipText={formatMessage({
              defaultMessage: `
                Conversion pixels are used to measure digital conversions in external landing pages using a single tag, either image or script.
                Permanence pixels in the other hand measure digital conversions but they acquire advanced logic to measure also different times of permanence in the external landing page.
                These permanence pixels will measure different permanence times: 5s, 10s, 15s, 30s, 60s, 90s and 120s`,
              id: 'menu.newPixel.pixelType.tooltip'
            })}
            type="simple"
            value={pixelTypeOptions.filter(pt => pt.id === type)}
          />
          <Selector
            className="newPixel__placement"
            id={`${BASE_ID}_placementTypeSelector`}
            label={formatMessage({
              defaultMessage: 'Placement Type',
              id: 'menu.newPixel.placementType.label'
            })}
            onChange={handleChangePlacement}
            options={pixelPlacementOptions}
            showSearch
            tooltipText={formatMessage({
              defaultMessage: `
                Visit identifies the user arrival into the external landing page.
                Section identifies specific browsing flows once the user is in a landing.
                Final identifies the final step, for instance a purchase in a browsing flow.
                (This is needed for the reporting).
              `,
              id: 'menu.newPixel.placementType.tooltip'
            })}
            type="simple"
            value={pixelPlacementOptions.filter(pp => pp.id === placement)}
          />
        </div>
      </div>
    </ModalUI>
  );
}
